import { api } from './client';
import type { Page } from './catalogs';

export type WarehouseOrderDirection = 'IN' | 'OUT';
export type WarehouseOrderState =
  | 'DRAFT' | 'COMMITTEE_VERIFIED' | 'EXECUTED' | 'CANCELLED';

export interface WarehouseOrderItem {
  id?: string;
  line_number: number;
  nomenclature_id: string;
  characteristic_id?: string | null;
  series_id?: string | null;
  uom_id?: string | null;
  expected_quantity: string;
  actual_quantity?: string | null;
  variance_quantity?: string;
  variance_reason?: string | null;
  unit_price?: string | null;
  expected_total?: string;
  actual_total?: string;
  line_comment?: string | null;
}

export interface WarehouseOrderListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  direction: WarehouseOrderDirection;
  state: WarehouseOrderState;
  warehouse_id: string;
  contractor_id: string | null;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
}

export interface WarehouseOrder extends WarehouseOrderListItem {
  contract_id: string | null;
  related_supplier_order_id: string | null;
  related_customer_order_id: string | null;
  related_goods_receipt_id: string | null;
  related_goods_sale_id: string | null;
  committee_chairperson_id: string | null;
  committee_verified_at: string | null;
  executed_at: string | null;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  comment: string | null;
  items: WarehouseOrderItem[];
  posted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface WarehouseOrderCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  comment?: string | null;
  direction: WarehouseOrderDirection;
  warehouse_id: string;
  contractor_id?: string | null;
  contract_id?: string | null;
  related_supplier_order_id?: string | null;
  related_customer_order_id?: string | null;
  committee_chairperson_id?: string | null;
  items: WarehouseOrderItem[];
}

export type WarehouseOrderUpdate = Partial<Omit<WarehouseOrderCreate,
  'organization_id' | 'direction' | 'items' |
  'related_supplier_order_id' | 'related_customer_order_id'>>;

export interface WarehouseOrderItemUpdate {
  actual_quantity?: string | null;
  variance_reason?: string | null;
  line_comment?: string | null;
  unit_price?: string | null;
}

const BASE = '/documents/warehouse-orders';

export const warehouseOrders = {
  list: (params?: {
    organization_id?: string;
    direction?: WarehouseOrderDirection;
    state?: WarehouseOrderState;
    warehouse_id?: string;
    contractor_id?: string;
    posted?: boolean;
    limit?: number; offset?: number;
    date_from?: string; date_to?: string;
    sort_by?: string; sort_dir?: 'asc' | 'desc';
  }) =>
    api.get<Page<WarehouseOrderListItem>>(BASE, { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<WarehouseOrder>(`${BASE}/${id}`).then((r) => r.data),
  create: (payload: WarehouseOrderCreate) =>
    api.post<WarehouseOrder>(BASE, payload).then((r) => r.data),
  update: (id: string, payload: WarehouseOrderUpdate) =>
    api.patch<WarehouseOrder>(`${BASE}/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`${BASE}/${id}`).then(() => undefined),
  addItem: (id: string, payload: WarehouseOrderItem) =>
    api.post<WarehouseOrderItem>(`${BASE}/${id}/items`, payload).then((r) => r.data),
  updateItem: (id: string, itemId: string, payload: WarehouseOrderItemUpdate) =>
    api.patch<WarehouseOrderItem>(`${BASE}/${id}/items/${itemId}`, payload).then((r) => r.data),
  removeItem: (id: string, itemId: string) =>
    api.delete(`${BASE}/${id}/items/${itemId}`).then(() => undefined),
  verify: (id: string) =>
    api.post<WarehouseOrder>(`${BASE}/${id}/verify-by-committee`).then((r) => r.data),
  execute: (id: string) =>
    api.post<WarehouseOrder>(`${BASE}/${id}/execute`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post<WarehouseOrder>(`${BASE}/${id}/cancel`, {
      cancellation_reason: reason,
    }).then((r) => r.data),
  post: (id: string) =>
    api.post<WarehouseOrder>(`${BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<WarehouseOrder>(`${BASE}/${id}/unpost`).then((r) => r.data),
};
